import { Text, Box, Button, Table, Thead, Tbody, Tr, Th, Td, Icon, VStack, useBreakpointValue, Link } from '@chakra-ui/react';
import { CheckIcon } from '@chakra-ui/icons';
import { JETBRAINS_PLUGIN_URL } from '../constants/urls';

interface PricingFeature {
  feature: string;
  description?: string;
  free: string | boolean | { type: "link"; text: string; url: string };
  pro: string | boolean | { type: "link"; text: string; url: string };
  enterprise: string | boolean | { type: "link"; text: string; url: string };
}

const PricingPage: React.FC = () => {
  const isMobile = useBreakpointValue({ base: true, md: false });
  
  const features: PricingFeature[] = [
    {
      feature: "Chat and Agent",
      free: "Limited",
      pro: "200",
      enterprise: "Unlimited (Usage Based)"
    },
    {
      feature: "Next-Edit Autocomplete",
      description: "The best autocomplete in JetBrains",
      free: "Limited",
      pro: "Unlimited",
      enterprise: "Unlimited"
    },
    {
      feature: "Zero Data Retention",
      description: "Sweep does not retain any code or user data",
      free: { type: "link", text: "Privacy Mode Available", url: "https://docs.sweep.dev/privacy" },
      pro: "Zero Data Retention",
      enterprise: "Zero Data Retention"
    }
  ];

  const plans = [
    {
      name: "Free",
      price: "$0",
      period: "per month",
      buttonText: "Get Started",
      buttonUrl: JETBRAINS_PLUGIN_URL,
      bg: "rgba(51, 40, 91, 0.3)"
    },
    {
      name: "Pro",
      price: "$20",
      period: "per month",
      buttonText: "Get Started",
      buttonUrl: JETBRAINS_PLUGIN_URL,
      bg: "rgba(51, 40, 91, 1)"
    },
    {
      name: "Enterprise",
      price: "$60",
      period: "per month",
      buttonText: "Contact Sales",
      buttonUrl: "https://calendly.com/william-sweep/call-with-william",
      bg: "radial-gradient(circle, rgba(51, 40, 91, 0.08) 0%, rgba(31, 22, 60, 0.16) 0%, rgba(41, 40, 91, 0.18) 50%)"
    }
  ];

  const renderFeatureValue = (value: string | boolean | { type: "link"; text: string; url: string }) => {
    if (typeof value === 'boolean') {
      return value ? (
        <Icon
          as={CheckIcon}
          color="white"
          boxSize={{ base: 6, md: 8 }}
          p={1.5}
          bg="green.500"
          borderRadius="full"
        />
      ) : null;
    }
    if (typeof value === 'object' && value.type === 'link') {
      return (
        <Link
          href={value.url}
          isExternal
          color="blue.300"
          textDecoration="underline"
          _hover={{ color: "blue.200" }}
        >
          {value.text}
        </Link>
      );
    }
    return <Text>{value as string}</Text>;
  };

  return (
    <Box
      p={{ base: 4, md: 8 }}
      backgroundColor="transparent"
      py={12}
      display="flex"
      flexDirection="column"
      alignItems="center"
    >
      <Text fontSize={{ base: "4xl", md: "6xl" }} fontWeight="bold" textAlign="center">
        Plans & Pricing
      </Text>
      <Text fontSize={{ base: "xl", md: "2xl" }} mb={8} textAlign="center">
        Choose the plan that works for you
      </Text>

      {isMobile ? (
        // Mobile: Card Layout
        <VStack spacing={8} width="100%" maxWidth="400px">
          {plans.map((plan, planIndex) => (
            <Box
              key={planIndex}
              bg={plan.bg}
              borderRadius="lg"
              p={6}
              width="100%"
              border="1px solid"
              borderColor="gray.600"
            >
              <VStack spacing={4}>
                <Text fontSize="2xl" fontWeight="bold" color="white">
                  {plan.name}
                </Text>
                <Box textAlign="center">
                  <Text fontSize="3xl" fontWeight="bold" color="white">
                    {plan.price}
                  </Text>
                  <Text fontSize="md" color="gray.300">
                    {plan.period}
                  </Text>
                </Box>
                <Button
                  colorScheme="purple"
                  bgColor="gray.100"
                  onClick={() => window.open(plan.buttonUrl, "_blank")}
                  size="md"
                  width="100%"
                >
                  {plan.buttonText}
                </Button>
                <VStack spacing={3} width="100%" align="stretch">
                  {features.map((feature, featureIndex) => (
                    <Box key={featureIndex} p={3} bg="rgba(255,255,255,0.05)" borderRadius="md">
                      <Text fontSize="sm" fontWeight="bold" color="white" mb={1}>
                        {feature.feature}
                      </Text>
                      {feature.description && (
                        <Text fontSize="xs" color="gray.300" mb={2}>
                          {feature.description}
                        </Text>
                      )}
                      <Box textAlign="center">
                        {renderFeatureValue(
                          planIndex === 0 ? feature.free :
                          planIndex === 1 ? feature.pro :
                          feature.enterprise
                        )}
                      </Box>
                    </Box>
                  ))}
                </VStack>
              </VStack>
            </Box>
          ))}
        </VStack>
      ) : (
        // Desktop: Table Layout
        <Box overflowX="auto" width="100%" maxWidth="1400px">
          <Table variant="simple" size="md" width="100%">
            <Thead>
              <Tr>
                <Th fontSize="xl" color="white" px={6} width="40%">Features</Th>
                <Th fontSize="lg" textAlign="center" px={6} width="20%"
                    bg="rgba(51, 40, 91, 0.3)" borderRadius="md">
                  Free
                </Th>
                <Th fontSize="lg" textAlign="center" px={6} width="20%"
                    bg="rgba(51, 40, 91, 1)" borderRadius="md">
                  Pro
                </Th>
                <Th fontSize="lg" textAlign="center" px={6} width="20%"
                    bg="radial-gradient(circle, rgba(51, 40, 91, 0.08) 0%, rgba(31, 22, 60, 0.16) 0%, rgba(41, 40, 91, 0.18) 50%)" borderRadius="md">
                  Enterprise
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              <Tr>
                <Td py={6} px={6}>
                  <Text as="h2" fontSize="xl" fontWeight="bold" mb={2} color="white">
                    Pricing
                  </Text>
                </Td>
                <Td textAlign="center" py={6} px={6} bg="rgba(51, 40, 91, 0.3)">
                  <Text fontSize="2xl" fontWeight="bold">$0</Text>
                  <Text fontSize="lg" color="gray.300">per month</Text>
                  <Button
                    colorScheme="purple"
                    bgColor="gray.100"
                    onClick={() => window.open(JETBRAINS_PLUGIN_URL, "_blank")}
                    size="md"
                    px={4}
                    mt={4}
                  >
                    Get Started
                  </Button>
                </Td>
                <Td textAlign="center" py={6} px={6} bg="rgba(51, 40, 91, 1)">
                  <Text fontSize="2xl" fontWeight="bold">$20</Text>
                  <Text fontSize="lg" color="gray.300">per month</Text>
                  <Button
                    colorScheme="purple"
                    bgColor="gray.100"
                    onClick={() => window.open(JETBRAINS_PLUGIN_URL, "_blank")}
                    size="md"
                    px={4}
                    mt={4}
                  >
                    Get Started
                  </Button>
                </Td>
                <Td textAlign="center" py={6} px={6}
                    bg="radial-gradient(circle, rgba(51, 40, 91, 0.08) 0%, rgba(31, 22, 60, 0.16) 0%, rgba(41, 40, 91, 0.18) 50%)">
                  <Text fontSize="2xl" fontWeight="bold">$60</Text>
                  <Text fontSize="lg" color="gray.300">per month</Text>
                  <Button
                    colorScheme="purple"
                    bgColor="gray.100"
                    onClick={() => window.open("https://calendly.com/william-sweep/call-with-william", "_blank")}
                    size="md"
                    px={4}
                    mt={4}
                  >
                    Contact Sales
                  </Button>
                </Td>
              </Tr>
              {features.map((item, index) => (
                <Tr key={index}>
                  <Td py={6} px={6}>
                    <Text as="h2" fontSize="xl" fontWeight="bold" mb={2} color="white">
                      {item.feature}
                    </Text>
                    {item.description && (
                      <Text color="gray.300" fontSize="md">
                        {item.description}
                      </Text>
                    )}
                  </Td>
                  <Td textAlign="center" py={6} px={6} bg="rgba(51, 40, 91, 0.3)">
                    {renderFeatureValue(item.free)}
                  </Td>
                  <Td textAlign="center" py={6} px={6} bg="rgba(51, 40, 91, 1)">
                    {renderFeatureValue(item.pro)}
                  </Td>
                  <Td textAlign="center" py={6} px={6}
                      bg="radial-gradient(circle, rgba(51, 40, 91, 0.08) 0%, rgba(31, 22, 60, 0.16) 0%, rgba(41, 40, 91, 0.18) 50%)">
                    {renderFeatureValue(item.enterprise)}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}
    </Box>
  );
};

export default PricingPage;